import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, FormGroup } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MultiSelect } from 'react-multi-select-component';
import { cloneDeep, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as systemMasterAction from '../SystemMasters/Store/SystemMasters.action';
import * as colourAction from '../Library/ColorLibary/store/ColorLibrary.action';
import { getUserSuppliers } from '../Dashboard/TodoList/TodoCommon.action';
import { createOptions, checkArray, converDropdownDataFilterData, mergerYearOptionsToStringValue, mergedMonthYearOptions } from '../../Components/helper';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import { useCPDetailState } from './contextProvider/cpDetailProvider';
import { CurrentYearItemRenderer } from '../../Components/Utility/CurrentYearDropdown';

function PCPFilters({ applyFilter, filterState, setFilterState }) {
  const { detailState } = useCPDetailState();
  const reducer = useSelector((reducerState) => reducerState);
  const { pcpId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const keys = [
    'brandId',
    'seasonId',
    'productType',
    'catOneId',
    'catTwoId',
    'catThreeId',
    'catFourId',
    'supplierId',
    'month',
    'year',
    'status',
    'createdBy',
    'dynamicTypes',
    'textCustomAttribute',
    'productIdentifiers',
    'selectedColumnShow',
    'licensorId',
    'licenseId',
    'likenessId',
    'colorwayId',
    'colorGroupId',
  ];
  const { UserDetails, SystemMastersDetails, BomLibraryDetails, Measuremets, ProductCPReducer, ColorLibraryDetails } = reducer;
  const { userInfo, isSupplierLoggedIn } = UserDetails;
  const { adminUsers, companyUsers } = Measuremets;
  const { ReferenceSupplier } = BomLibraryDetails;
  const { individualDetail } = ProductCPReducer;
  const { ColorWaysLibList, ColorLibGroup: colorLibList } = ColorLibraryDetails;
  const {
    brandList,
    category1List,
    category2List,
    pcTypeList,
    productStatusList,
    seasonList,
    category3List,
    category4List,
    customAttributeDetail,
    productIdentifierList,
    identifierType,
    multiSubBrandList,
    licensorList,
    licenseList,
    likenessList,
    subBrandAccess,
  } = SystemMastersDetails;

  const [userSuppliers, setUserSuppliers] = useState([]);

  // Fetch sub-brand access on component mount
  useEffect(() => {
    dispatch(systemMasterAction.getSubBrandAccess());
  }, [dispatch]);

  useEffect(() => {
    const fetchUserSuppliers = async () => {
      const data = await dispatch(getUserSuppliers());
      const suppliers = checkArray(data?.suppliers)
        ? data.suppliers.map((supplier) => ({
            _id: supplier._id,
            name: supplier?.companies?.length ? supplier.companies[0].supplierName : supplier.name,
          }))
        : [];
      setUserSuppliers(suppliers);
    };
    if (!isSupplierLoggedIn) {
      fetchUserSuppliers();
    }
  }, [dispatch, isSupplierLoggedIn]);

  // Fetch colorways
  useEffect(() => {
    const fetchColorways = async () => {
      await dispatch(
        colourAction.fetchColorListLibrary({
          loggedInUserId: userInfo?.userId,
        }),
      );
    };
    if (userInfo?.userId) {
      fetchColorways();
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    const fetchColorGroups = async () => {
      if (userInfo?.userId) {
        await dispatch(
          colourAction.fetchColorGroupLibrary({
            loggedInUserId: userInfo.userId,
            isDetails: true,
          }),
        );
      }
    };
    fetchColorGroups();
  }, [dispatch, userInfo?.userId]);

  // Initial load - get licensors
  useEffect(() => {
    const fetchLicenses = async () => {
      if (filterState?.licensorId?.length > 0) {
        await dispatch(
          systemMasterAction.getLicenseDetails({
            licensorIds: filterState.licensorId,
            loggedInUserId: userInfo?.userId,
            isActive: true,
          }),
        );
      }
    };

    const fetchLikenesses = async () => {
      if (filterState?.licensorId?.length > 0 && filterState?.licenseId?.length > 0) {
        await dispatch(
          systemMasterAction.getLikenessDetails({
            licensorIds: filterState.licensorId,
            licenseIds: filterState.licenseId,
            loggedInUserId: userInfo?.userId,
            isActive: true,
          }),
        );
      }
    };

    const fetchData = async () => {
      await fetchLicenses();
      await fetchLikenesses();
    };

    fetchData();
  }, [dispatch, userInfo?.userId, filterState?.licensorId, filterState?.licenseId]);
  const [isApply, setIsApply] = useState(() => {
    const filters = JSON.parse(localStorage.getItem(`pcpFilters_${pcpId}`));
    return keys.some((key) => {
      const value = filters?.[key];
      return value?.length > 0;
    });
  });
  const {
    monthOptions,
    yearOptions,
    activeBrand,
    activeSubBrand,
    activeProductType,
    activeCat1,
    activeCat2,
    activeCat3,
    activeCat4,
    activeSupplier,
    activeSeason,
    activeMonth,
    activeYear,
    activeStatus,
    activeCreatedBy,
    dynamicTypes,
    textCustomAttribute,
    productIdentifiers,
    activeLicensor,
    activeLicense,
    activeLikeness,
    activeColorway,
    activeColorGroup,
  } = filterState;

  // update month options when year is selected or changed
  const monthYearBasedOptions = useMemo(() => {
    const months = monthOptions;
    const years = (activeYear || []).slice().sort((a, b) => a - b); // sort years ascending
    return mergedMonthYearOptions(months, years);
  }, [activeYear, monthOptions]);

  // update month options when year is selected or changed
  const individualMonthYearBasedOptions = useMemo(() => {
    const months = individualDetail?.month;
    const years = (activeYear || []).slice().sort((a, b) => a - b); // sort years ascending
    return mergerYearOptionsToStringValue(months, years);
  }, [activeYear, individualDetail?.month]);

  const handleFilterApply = () => {
    const filterObj = {
      brandId: activeBrand,
      subBrandId: activeSubBrand,
      productType: activeProductType,
      catOneId: activeCat1,
      catTwoId: activeCat2,
      catThreeId: activeCat3,
      catFourId: activeCat4,
      supplierId: activeSupplier,
      seasonId: activeSeason,
      month: activeMonth,
      year: activeYear.map((vl) => vl.toString()),
      status: activeStatus,
      createdBy: activeCreatedBy,
      dynamicTypes,
      textCustomAttribute,
      productIdentifiers,
      licensorId: activeLicensor,
      licenseId: activeLicense,
      likenessId: activeLikeness,
      colorwayId: activeColorway,
      colorGroupId: activeColorGroup,
    };
    setFilterState((prevState) => ({
      ...prevState,
      ...filterObj,
    }));
    localStorage.setItem(`pcpFilters_${pcpId}`, JSON.stringify(filterObj));
    setIsApply(true);
    applyFilter(filterObj);
  };
  useEffect(() => {
    if (checkArray(activeBrand)) {
      dispatch(
        systemMasterAction.getMultiSubBrandDetails({
          brandId: activeBrand,
        }),
      );
    }
  }, [activeBrand, dispatch]);

  useEffect(() => {
    if (checkArray(activeCat2) && checkArray(activeCat1) && userInfo?.userId) {
      dispatch(
        systemMasterAction.getCategory3Details({
          categoryOneId: activeCat1,
          categoryTwoId: activeCat2,
          categoryType: 3,
          isActive: true,
          loggedInUserId: userInfo?.userId,
        }),
      );
    }
  }, [activeCat2, activeCat1, dispatch, userInfo?.userId]);

  useEffect(() => {
    if (checkArray(activeCat2) && checkArray(activeCat1) && userInfo?.userId && checkArray(activeCat3)) {
      dispatch(
        systemMasterAction.getCategory4Details({
          categoryOneId: activeCat1,
          categoryTwoId: activeCat2,
          categoryThreeId: activeCat3,
          categoryType: 4,
          isActive: true,
          loggedInUserId: userInfo?.userId,
        }),
      );
    }
  }, [activeCat2, activeCat1, activeCat3, dispatch, userInfo?.userId]);

  const handleFilterLicense = (val, key) => {
    setFilterState((prevState) => {
      const newState = { ...prevState, [key]: val };

      if (key === 'activeLicensor') {
        newState.activeLicense = [];
        newState.activeLikeness = [];

        if (Array.isArray(val) && val.length > 0) {
          dispatch(
            systemMasterAction.getLicenseDetails({
              licensorIds: val,
              loggedInUserId: userInfo?.userId,
              isActive: true,
            }),
          );
        }
        dispatch(systemMasterAction.clearLicenseData());
        dispatch(systemMasterAction.clearLikenessData());
      }

      if (key === 'activeLicense') {
        newState.activeLikeness = [];

        if (Array.isArray(val) && val.length > 0 && checkArray(filterState.activeLicensor)) {
          dispatch(
            systemMasterAction.getLikenessDetails({
              licensorIds: filterState.activeLicensor,
              licenseIds: val,
              loggedInUserId: userInfo?.userId,
              isActive: true,
            }),
          );
        }
        dispatch(systemMasterAction.clearLikenessData());
      }

      return newState;
    });
  };

  const handleFilterSelect = (val, key) => {
    if (['activeCat2', 'activeCat1']?.includes(key)) {
      setFilterState((prevState) => ({
        ...prevState,
        activeCat3: [],
        activeCat4: [],
        [key]: val,
      }));
    } else if (key === 'activeBrand') {
      setFilterState((prevState) => {
        const validSubBrands = multiSubBrandList?.filter((sb) => val.includes(sb.brandId))?.map((sb) => sb._id);
        return {
          ...prevState,
          [key]: val,
          activeSubBrand: prevState.activeSubBrand?.filter((sub) => validSubBrands?.includes(sub)) || [],
        };
      });
    } else if (['activeYear'].includes(key)) {
      const remainingYears = val || [];
      // filter activeMonth to keep only months that match remaining years
      const validMonths = (activeMonth || []).filter((monthStr) => {
        const match = monthStr.match(/^([a-zA-Z]+)_(\d{4})$/);
        if (!match) return true; // keep if no year part
        const year = Number(match[1]);
        return remainingYears.includes(year);
      });
      setFilterState((prevState) => ({
        ...prevState,
        activeMonth: validMonths,
        activeYear: val,
      }));
    } else {
      setFilterState((prevState) => ({
        ...prevState,
        [key]: val,
      }));
    }
  };

  const handleFilterDynamicSelect = (items, colId) => {
    const isColDataExist = dynamicTypes?.filter((e) => e.colId === colId);
    const copyState = cloneDeep(dynamicTypes);
    if (checkArray(isColDataExist)) {
      isColDataExist.forEach((ele) => {
        copyState.splice(
          copyState.findIndex((e) => e.valId === ele.valId),
          1,
        );
      });
    }
    const obj =
      checkArray(items) &&
      items.map((valId) => {
        return { colId, valId };
      });

    setFilterState((prevState) => ({
      ...prevState,
      dynamicTypes: obj ? [...copyState, ...obj] : [...copyState],
    }));
  };

  const handleFilterClear = () => {
    const filterObj = {
      activeBrand: [],
      activeSubBrand: [],
      activeProductType: [],
      activeCat1: [],
      activeCat2: [],
      activeCat3: [],
      activeCat4: [],
      activeSupplier: [],
      activeSeason: [],
      activeMonth: [],
      activeYear: [],
      activeStatus: [],
      activeCreatedBy: [],
      textCustomAttribute: [],
      productIdentifiers: [],
      dynamicTypes: [],
      activeLicensor: [],
      activeLicense: [],
      activeLikeness: [],
      activeColorway: [],
      activeColorGroup: [],
    };
    localStorage.removeItem(`pcpFilters_${pcpId}`);
    setIsApply(false);

    setFilterState((prevState) => ({
      ...prevState,
      ...filterObj,
    }));
    applyFilter(filterObj);
  };

  const brandOptions = useMemo(() => {
    if (checkArray(individualDetail?.brand) && checkArray(brandList)) {
      const brandId = individualDetail?.brand?.map((e) => e?._id?.toString());
      return createOptions(
        brandList?.filter((e) => brandId?.includes(e._id.toString())),
        'name',
        '_id',
      );
    }
    if (checkArray(brandList)) {
      return createOptions(brandList, 'name', '_id');
    }
    return [];
  }, [brandList, individualDetail?.brand]);

  const subBrandOptions = useMemo(() => {
    if (checkArray(individualDetail?.subBrand) && checkArray(multiSubBrandList)) {
      const brandId = individualDetail?.subBrand?.map((e) => e?._id?.toString());
      return createOptions(
        multiSubBrandList?.filter((e) => brandId?.includes(e._id.toString())),
        'name',
        '_id',
      );
    }
    if (checkArray(multiSubBrandList)) {
      return createOptions(multiSubBrandList, 'name', '_id');
    }
    return [];
  }, [multiSubBrandList, individualDetail?.subBrand]);

  const category1Options = useMemo(() => {
    if (checkArray(individualDetail?.catOneId) && checkArray(category1List)) {
      const catOneId = individualDetail?.catOneId?.map((e) => e?._id?.toString());
      return createOptions(
        category1List?.filter((e) => catOneId?.includes(e._id)),
        'name',
        '_id',
      );
    }
    if (checkArray(category1List)) return createOptions(category1List, 'name', '_id');
    return [];
  }, [category1List, individualDetail?.catOneId?.length > 0]);

  const category2Options = useMemo(() => {
    if (checkArray(individualDetail?.catTwoId) && checkArray(category2List)) {
      const catTwoId = individualDetail?.catTwoId?.map((e) => e?._id?.toString());
      return createOptions(
        category2List?.filter((e) => catTwoId?.includes(e._id)),
        'name',
        '_id',
      );
    }
    if (checkArray(category2List)) return createOptions(category2List, 'name', '_id');
    return [];
  }, [category2List, individualDetail?.catTwoId?.length > 0]);

  const productTypeOptions = useMemo(() => {
    if (checkArray(individualDetail?.productType) && checkArray(pcTypeList)) {
      const productType = individualDetail?.productType?.map((e) => e?._id?.toString());
      return createOptions(
        pcTypeList?.filter((e) => productType?.includes(e._id)),
        'name',
        '_id',
      );
    }
    if (checkArray(pcTypeList)) return createOptions(pcTypeList, 'name', '_id');
    return [];
  }, [pcTypeList, individualDetail?.productType?.length > 0]);

  const productStatusOptions = useMemo(() => {
    if (checkArray(individualDetail?.status) && checkArray(productStatusList)) {
      return createOptions(
        productStatusList?.filter((e) => individualDetail?.status?.includes(e._id)),
        'name',
        '_id',
      );
    }
    if (checkArray(productStatusList)) return createOptions(productStatusList, 'name', '_id');
    return [];
  }, [productStatusList, individualDetail?.status?.length > 0]);

  const seasonOptions = useMemo(() => {
    if (checkArray(individualDetail?.season) && checkArray(seasonList)) {
      const season = individualDetail?.season?.map((e) => e?._id?.toString());
      return createOptions(
        seasonList?.filter((e) => season?.includes(e._id)),
        'name',
        '_id',
      );
    }
    if (checkArray(seasonList)) return createOptions(seasonList, 'name', '_id');
    return [];
  }, [seasonList, individualDetail?.season?.length > 0]);

  const supplierOptions = useMemo(() => {
    const allowedSuppliers = isSupplierLoggedIn ? ReferenceSupplier : userSuppliers;
    if (!checkArray(allowedSuppliers)) return [];
    if (checkArray(individualDetail?.suppliers)) {
      const suppliers = individualDetail?.suppliers?.map((e) => e?._id?.toString());
      return createOptions(
        allowedSuppliers?.filter((e) => suppliers?.includes(e._id)),
        'name',
        '_id',
      );
    }
    return createOptions(allowedSuppliers, 'name', '_id');
  }, [ReferenceSupplier, userSuppliers, isSupplierLoggedIn, individualDetail?.suppliers?.length > 0]);

  const dynamicFields = useMemo(() => {
    if (checkArray(customAttributeDetail)) return converDropdownDataFilterData(customAttributeDetail);
    return [];
  }, [customAttributeDetail]);

  const category3Options = useMemo(() => {
    if (checkArray(individualDetail?.catThreeId) && checkArray(category3List)) {
      const catThreeId = individualDetail?.catThreeId?.map((e) => e?._id?.toString());
      return createOptions(
        category3List?.filter((e) => catThreeId?.includes(e._id)),
        'name',
        '_id',
      );
    }
    if (checkArray(category3List)) return createOptions(category3List, 'name', '_id');
    return [];
  }, [category3List, individualDetail?.catThreeId?.length > 0]);

  const category4Options = useMemo(() => {
    if (checkArray(individualDetail?.catFourId) && checkArray(category4List)) {
      const catFourId = individualDetail?.catFourId?.map((e) => e?._id?.toString());
      return createOptions(
        category4List?.filter((e) => catFourId?.includes(e._id)),
        'name',
        '_id',
      );
    }
    if (checkArray(category4List)) return createOptions(category4List, 'name', '_id');
    return [];
  }, [category4List, individualDetail?.catFourId?.length > 0]);

  const licensorOptions = useMemo(() => {
    if (checkArray(licensorList)) return createOptions(licensorList, 'name', '_id');
    return [];
  }, [licensorList]);

  const licenseOptions = useMemo(() => {
    if (checkArray(licenseList)) return createOptions(licenseList, 'name', '_id');
    return [];
  }, [licenseList]);

  const likenessOptions = useMemo(() => {
    if (checkArray(likenessList)) return createOptions(likenessList, 'name', '_id');
    return [];
  }, [likenessList]);

  const colorwayOptions = useMemo(() => {
    if (checkArray(ColorWaysLibList)) return createOptions(ColorWaysLibList, 'refName', '_id');
    return [];
  }, [ColorWaysLibList]);

  const colorGroupOptions = useMemo(() => {
    if (checkArray(colorLibList)) {
      return createOptions(colorLibList, 'name', '_id');
    }
    return [];
  }, [colorLibList]);

  const createdByOption = useMemo(() => {
    const allUser = [adminUsers, ...companyUsers];
    if (checkArray(allUser)) {
      return allUser?.map((ele) => {
        return {
          label: `${ele?.firstName} ${ele?.lastName}`,
          value: ele?._id,
        };
      });
    }
    return [];
  }, [adminUsers, companyUsers]);

  const TextCustomAttributeOptions = useMemo(() => {
    let options = [];
    if (checkArray(customAttributeDetail)) {
      options = customAttributeDetail?.filter((ele) => ele?.type === 'text' && ele?.isLable === false)?.map((e) => ({ label: e.keyName, value: e._id }));
    }
    return options;
  }, [customAttributeDetail]);

  const ProductIdentifierOptions = useMemo(() => {
    let options = [];
    if (checkArray(productIdentifierList)) {
      options = productIdentifierList
        ?.filter((ele) => (identifierType ? !['Product number', 'Colourway', 'Size', 'Length']?.includes(ele?.name) : ele?.name !== 'Product number'))
        ?.map((e) => ({ label: e?.name, value: e?._id }));
    }
    return options;
  }, [productIdentifierList, identifierType]);

  useEffect(() => {
    const brandIds = Array.isArray(activeBrand) ? activeBrand : [activeBrand];
    const productTypeIds = Array.isArray(activeProductType) ? activeProductType : [activeProductType];
    let fieldsToKeep = [];
    if (brandIds.filter(Boolean).length > 0 && productTypeIds.filter(Boolean).length > 0) {
      fieldsToKeep = dynamicFields.filter((ele) => {
        const matchesBrand = ele?.brand?.length ? ele.brand.some((br) => brandIds.includes(br)) : false;
        const matchesProductType = ele?.productType?.length ? ele.productType.some((pt) => productTypeIds.includes(pt)) : false;
        return matchesBrand && matchesProductType;
      });
    }
    const validFieldIds = fieldsToKeep.map((field) => field._id);
    setFilterState((prevState) => ({
      ...prevState,
      dynamicTypes: prevState.dynamicTypes.filter((d) => validFieldIds.includes(d.colId)),
    }));
  }, [activeBrand, activeProductType, dynamicFields]);

  return (
    <CustomTooltip placement="top" tooltipText={t('FILTER.FILTER')}>
      <button
        type="button"
        aria-label={t('FILTER.SORT')}
        className={`${isApply ? 'active' : ''} btn`}
        disabled={detailState?.loading || detailState?.tableLoading || detailState?.isMainLoading}
      >
        <OverlayTrigger
          placement="bottom"
          trigger="click"
          rootClose
          overlay={
            <Popover className="custom-popover popover-filter">
              <Popover.Content>
                <Row className="form-row">
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={brandOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={brandOptions?.filter((val) => activeBrand?.includes(val?._id)) || []}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_BRAND'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeBrand',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  {subBrandAccess && (
                    <Col md={2}>
                      <FormGroup>
                        <MultiSelect
                          name="group"
                          options={subBrandOptions.filter((sb) => activeBrand.includes(sb.brandId))}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={subBrandOptions?.filter((val) => activeSubBrand?.includes(val?._id)) || []}
                          labelledBy="Select Sub Brand"
                          overrideStrings={{
                            selectSomeItems: 'Select Sub Brand',
                          }}
                          onChange={(selected) =>
                            handleFilterSelect(
                              selected.map((val) => val.value),
                              'activeSubBrand',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={productTypeOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={productTypeOptions.filter((val) => checkArray(productTypeOptions) && activeProductType?.includes(val.value)) || []}
                        labelledBy={t('FILTER.SELECT_PRODUCT_TYPE')}
                        overrideStrings={{
                          selectSomeItems: t('FILTER.SELECT_PRODUCT_TYPE'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeProductType',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={category1Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={category1Options.filter((val) => checkArray(category1Options) && activeCat1?.includes(val.value)) || []}
                        labelledBy={t('FILTER.SELECT_CATEGORY_ONE')}
                        overrideStrings={{
                          selectSomeItems: t('FILTER.SELECT_CATEGORY_ONE'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeCat1',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={category2Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={category2Options.filter((val) => checkArray(category2Options) && activeCat2?.includes(val.value)) || []}
                        labelledBy={t('FILTER.SELECT_CATEGORY_TWO')}
                        overrideStrings={{
                          selectSomeItems: t('FILTER.SELECT_CATEGORY_TWO'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeCat2',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={category3Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={category3Options.filter((val) => checkArray(category3Options) && activeCat3?.includes(val.value)) || []}
                        labelledBy={t('FILTER.SELECT_CATEGORY_THREE')}
                        overrideStrings={{
                          selectSomeItems: t('FILTER.SELECT_CATEGORY_THREE'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeCat3',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={category4Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={category4Options?.filter((val) => checkArray(category4Options) && activeCat4?.includes(val.value)) || []}
                        labelledBy={t('FILTER.SELECT_CATEGORY_FOUR')}
                        overrideStrings={{
                          selectSomeItems: t('FILTER.SELECT_CATEGORY_FOUR'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeCat4',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  {!isSupplierLoggedIn && (
                    <Col md={2}>
                      <FormGroup>
                        <MultiSelect
                          name="group"
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={supplierOptions?.filter((val) => checkArray(supplierOptions) && activeSupplier?.includes(val.value)) || []}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                          }}
                          onChange={(selected) =>
                            handleFilterSelect(
                              selected.map((val) => val.value),
                              'activeSupplier',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={seasonOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={seasonOptions?.filter((val) => checkArray(seasonOptions) && activeSeason?.includes(val.value)) || []}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SEASON')}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SEASON'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeSeason',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        ItemRenderer={CurrentYearItemRenderer}
                        options={checkArray(individualDetail?.year) ? yearOptions?.filter((val) => individualDetail.year?.includes(val.label)) : yearOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={yearOptions?.filter((val) => checkArray(activeYear) && activeYear?.includes(val.value.toString())) || []}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_YEAR')}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_YEAR'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value.toString()),
                            'activeYear',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={
                          checkArray(individualMonthYearBasedOptions)
                            ? monthYearBasedOptions?.filter((val) => individualMonthYearBasedOptions?.includes(val.value))
                            : monthYearBasedOptions
                        }
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={monthYearBasedOptions?.filter((val) => checkArray(activeMonth) && activeMonth?.includes(val.value)) || []}
                        labelledBy={t('PROGRESS_INDICATOR.SELECT_MONTH')}
                        overrideStrings={{
                          selectSomeItems: t('PROGRESS_INDICATOR.SELECT_MONTH'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeMonth',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  {!isEmpty(dynamicFields) &&
                    dynamicFields?.map((ele) => {
                      const brandIds = Array.isArray(activeBrand) ? activeBrand : [activeBrand];
                      const productTypeIds = Array.isArray(activeProductType) ? activeProductType : [activeProductType];
                      const matchesBrand = ele?.brand?.length ? ele.brand.some((br) => brandIds.includes(br)) : false;
                      const matchesProductType = ele?.productType?.length ? ele.productType.some((pt) => productTypeIds.includes(pt)) : false;
                      let isEnabled = true;
                      if (brandIds.filter(Boolean).length > 0 && productTypeIds.filter(Boolean).length > 0) {
                        isEnabled = matchesBrand && matchesProductType;
                      }

                      let selectOpt = [];
                      if (ele.type === 'dropdown') {
                        selectOpt = createOptions(ele.values, 'keyName', '_id');
                      }
                      if (ele.type === 'dropdown' && ele.isActive) {
                        return (
                          <Col md={2} key={ele?._id}>
                            <FormGroup>
                              <div className="custom-react-select ">
                                <MultiSelect
                                  name="group"
                                  options={selectOpt}
                                  disabled={!isEnabled}
                                  className="product-custom-multiselect common-new-multiselect w-100"
                                  value={isEnabled ? selectOpt?.filter((val) => dynamicTypes?.some((e) => e.valId === val._id && e.colId === ele._id)) : []}
                                  labelledBy={`Select ${ele.keyName}`}
                                  overrideStrings={{
                                    selectSomeItems: `Select ${ele.keyName}`,
                                  }}
                                  onChange={(selected) =>
                                    handleFilterDynamicSelect(
                                      selected.map((val) => val._id),
                                      ele?._id,
                                    )
                                  }
                                />
                              </div>
                            </FormGroup>
                          </Col>
                        );
                      }
                      return null;
                    })}
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={productStatusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={productStatusOptions?.filter((val) => checkArray(productStatusOptions) && activeStatus?.includes(val.value)) || []}
                        labelledBy={t('PROGRESS_INDICATOR.SELECT_STATUS')}
                        overrideStrings={{
                          selectSomeItems: t('PROGRESS_INDICATOR.SELECT_STATUS'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeStatus',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={createdByOption}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={createdByOption?.filter((val) => checkArray(createdByOption) && activeCreatedBy?.includes(val.value)) || []}
                        labelledBy={t('PROGRESS_INDICATOR.SELECTED_CREATE')}
                        overrideStrings={{
                          selectSomeItems: t('PROGRESS_INDICATOR.SELECTED_CREATE'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeCreatedBy',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="textCustomAttribute"
                        options={TextCustomAttributeOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          TextCustomAttributeOptions?.filter((val) => checkArray(TextCustomAttributeOptions) && textCustomAttribute?.includes(val.value)) || []
                        }
                        labelledBy={t('DEVLOPMENT_ORDER.PRODUCT_INFORMATIONS.PRODUCT_FILTER.SELECT_TEXT_ATTRIBUTES')}
                        overrideStrings={{
                          selectSomeItems: t('DEVLOPMENT_ORDER.PRODUCT_INFORMATIONS.PRODUCT_FILTER.SELECT_TEXT_ATTRIBUTES'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'textCustomAttribute',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="productIdentifiers"
                        options={ProductIdentifierOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={ProductIdentifierOptions?.filter((val) => checkArray(ProductIdentifierOptions) && productIdentifiers?.includes(val.value)) || []}
                        labelledBy={t('DEVLOPMENT_ORDER.PRODUCT_INFORMATIONS.PRODUCT_FILTER.SELECT_PRODUCT_IDENTIFIERS')}
                        overrideStrings={{
                          selectSomeItems: t('DEVLOPMENT_ORDER.PRODUCT_INFORMATIONS.PRODUCT_FILTER.SELECT_PRODUCT_IDENTIFIERS'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'productIdentifiers',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={licensorOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={licensorOptions.filter((val) => activeLicensor?.includes(val._id))}
                        labelledBy={t('LIKENESS_MASTER.SELECT_LICENSOR')}
                        overrideStrings={{
                          selectSomeItems: t('LIKENESS_MASTER.SELECT_LICENSOR'),
                        }}
                        onChange={(selected) =>
                          handleFilterLicense(
                            selected.map((val) => val._id),
                            'activeLicensor',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={licenseOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={licenseOptions.filter((val) => activeLicense?.includes(val._id))}
                        labelledBy={t('LIKENESS_MASTER.SELECT_LICENSE')}
                        overrideStrings={{
                          selectSomeItems: t('LIKENESS_MASTER.SELECT_LICENSE'),
                        }}
                        onChange={(selected) =>
                          handleFilterLicense(
                            selected.map((val) => val._id),
                            'activeLicense',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={likenessOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={likenessOptions.filter((val) => Array.isArray(activeLikeness) && activeLikeness.some((id) => id === val._id))}
                        labelledBy={t('LIKENESS_MASTER.SELECT_LIKENESS')}
                        overrideStrings={{
                          selectSomeItems: t('LIKENESS_MASTER.SELECT_LIKENESS'),
                        }}
                        onChange={(selected) =>
                          handleFilterLicense(
                            selected.map((val) => val._id),
                            'activeLikeness',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="group"
                        options={colorwayOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={colorwayOptions.filter((val) => activeColorway?.includes(val._id))}
                        labelledBy={t('COMPONENT.PRODUCT_FILTER.FILTER.SELECT_COLOURWAY')}
                        overrideStrings={{
                          selectSomeItems: t('COMPONENT.PRODUCT_FILTER.FILTER.SELECT_COLOURWAY'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val._id),
                            'activeColorway',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <MultiSelect
                        name="colorGroup"
                        options={colorGroupOptions || []}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={colorGroupOptions.filter((val) => !isEmpty(activeColorGroup) && activeColorGroup.includes(val._id))}
                        labelledBy={t('COLOR_LIBRARY.FILTER.SELECT_COLOR_GROUP')}
                        overrideStrings={{
                          selectSomeItems: t('COLOR_LIBRARY.FILTER.SELECT_COLOR_GROUP'),
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val._id),
                            'activeColorGroup',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div className="d-flex justify-content-end">
                      <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.CLEAR')}>
                        <button
                          onClick={() => handleFilterClear()}
                          aria-label="Close"
                          className="btn p-0 d-inline-flex"
                          type="button"
                          disabled={detailState?.loading || detailState?.tableLoading || detailState?.isMainLoading}
                        >
                          <span className="icon-Clear-New f-24" />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.APPLY')}>
                        <button
                          onClick={() => handleFilterApply()}
                          aria-label="Apply"
                          className="btn p-0 d-inline-flex ml-16"
                          type="button"
                          disabled={detailState?.loading || detailState?.tableLoading || detailState?.isMainLoading}
                        >
                          <span className="icon-Tick-1 f-28" />
                        </button>
                      </CustomTooltip>
                    </div>
                  </Col>
                </Row>
              </Popover.Content>
            </Popover>
          }
        >
          <i className="icon-Filter f-30" />
        </OverlayTrigger>
      </button>
    </CustomTooltip>
  );
}

export default PCPFilters;
